import React, { createContext, useContext, useState, useEffect } from 'react';
import InviteCodeAPI from '../services/inviteCodeApi';
import ReferralAPI from '../services/referralApi';
import authApi from '../services/authApi';
import PointsAPI from '../services/pointsApi';
import { handleAuthError, resetRefreshState } from '../utils/authErrorHandler';
import { validateEmail, validatePassword, validateNickname, validateVerificationCode } from '../utils/validationUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');

    let isTokenValid = true;

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000;
        const now = Date.now();
        if (now >= exp) {
          isTokenValid = false;
        }
      } catch (e) {
        isTokenValid = false;
      }
    }

    if (!isTokenValid) {
      handleAuthError('登录已过期，请重新登录', false, true);
      setUser(null);
    } else if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    setIsLoading(false);
  }, []);

  // 验证邀请码
  const validateInviteCode = async (inviteCode) => {
    try {
      // 调用真实API验证邀请码
      const result = await InviteCodeAPI.validateInviteCode(inviteCode);
      
      if (result.valid) {
        return { 
          success: true, 
          valid: true,
          inviterId: result.inviter_id,
          inviterName: result.inviter_name
        };
      } else {
        return { 
          success: true, 
          valid: false,
          message: result.message || '邀请码无效或已过期'
        };
      }
    } catch (error) {
      console.error('邀请码验证失败:', error);
      throw new Error('邀请码验证失败，请稍后重试');
    }
  };

  const sendVerificationCode = async (email, type, captchaId = null, captchaCode = null) => {
    try {
      const data = { email, type };
      if (captchaId && captchaCode) {
        data.captcha_id = captchaId;
        data.captcha_code = captchaCode;
      }
      const result = await authApi.sendVerificationCode(data);
      return result;
    } catch (error) {
      throw new Error(error.message || '验证码发送失败，请稍后重试');
    }
  };


   const sendForgetPassword = async (email, type, captchaId, captchaCode) => {
    try {
      const result = await authApi.sendForgetPassword({ email, type, captcha_id: captchaId, captcha_code: captchaCode });
      return result;
    } catch (error) {
      throw error;
    }
  }; 

  const login = async (credential, password, loginType = 'password', inviteCode = null) => {
    try {
      setIsLoading(true);
      
      // 先清除旧的认证信息，避免账号冲突
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      
      if (loginType === 'email') {
        // 验证码登录
        const email = credential;
        const code = password; // 这里password参数实际是验证码
        
        // 验证验证码
        // if (!AuthAPI.verifyCode(email, code)) {
        //   throw new Error('验证码错误或已过期');
        // }
        
        // 调用后端API进行手机号登录
        try {
          const loginData = {
            email: email,
            verification_code: code,
            login_type: 'email'
          };
          
          const result = await authApi.login(loginData);
          
          if (result.success && result.user) {
            // 登录成功，重置token刷新状态
            resetRefreshState();
            
            // 登录成功
            const userData = {
              id: result.user.id,
              user_id: result.user.user_id,  // 重要：保存user_id用于支付等关键操作
              phone: result.user.phone,
              email: result.user.email,
              name: result.user.nickname,
              avatar: result.user.avatar,
              loginType: result.user.login_type,
              created_at: result.user.created_at
            };
            
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            
            // 存储access_token（重要：用于API认证）
            if (result.access_token) {
              localStorage.setItem('access_token', result.access_token);
            }
            
            
            return { success: true };
          } else {
            throw new Error('登录失败');
          }
        } catch (apiError) {
          // 如果API调用失败，检查是否是用户不存在
          if (apiError.message.includes('用户不存在') || apiError.message.includes('User not found')) {
            throw new Error('用户不存在');
          } else {
            throw apiError;
          }
        }

      } else if (loginType === 'password') {
        // 密码登录 - 调用真实的后端API
        try {
          const loginData = {
            email: credential,
            password: password,
            login_type: 'email'
          };
          
          const result = await authApi.login(loginData);
          
          if (result.success && result.user) {
            // 登录成功，重置token刷新状态
            resetRefreshState();
            
            // 登录成功
            const userData = {
              id: result.user.id,
              user_id: result.user.user_id,  // 重要：保存user_id用于支付等关键操作
              phone: result.user.phone,
              email: result.user.email,
              name: result.user.nickname,
              avatar: result.user.avatar,
              loginType: result.user.login_type,
              created_at: result.user.created_at
            };
            
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            
            // 存储access_token（重要：用于API认证）
            if (result.access_token) {
              localStorage.setItem('access_token', result.access_token);
            }
            
            return { success: true };
          } else {
            throw new Error('登录失败');
          }
        } catch (apiError) {
          // 如果API调用失败，检查是否是用户不存在或密码错误
          if (apiError.message.includes('用户不存在') || apiError.message.includes('User not found')) {
            throw new Error('用户不存在');
          } else if (apiError.message.includes('邮箱或密码错误') || apiError.message.includes('密码错误')) {
            throw new Error('邮箱或密码错误');
          } else {
            throw apiError;
          }
        }
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      
      // 先清除旧的认证信息，避免账号冲突
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      
      // 模拟API调用延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (userData.autoRegister) {
        // 自动注册（通过验证码登录触发）
        const email = userData.email;
        const code = userData.code;
        
        // 再次验证验证码（防止重复提交）
        // if (!AuthAPI.verifyCode(email, code)) {
        //   throw new Error('验证码错误或已过期');
        // }
        
        // 清除验证码
        // AuthAPI.clearVerificationCode(email);
        
        // 创建新用户 - 使用手机号后4位作为默认昵称
        // const userName = `用户${phone.slice(-4)}`;

        // 创建新用户，默认昵称使用邮箱前缀
        const userName = `${email}`;
        
        // 生成更安全的临时密码
        const generateSecurePassword = () => {
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
          let password = '';
          for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          return password;
        };
        
        // 调用后端API注册用户
        const registerData = {
          email: email,
          nickname: userName,
          password: generateSecurePassword(), // 更安全的临时密码
          login_type: userData.loginType || 'phone',
          avatar: null,
          verification_code: code
        };
        
        const apiResult = await authApi.register(registerData);
        
        // 注意：后端现在返回 UserLoginResponse (包含 user 对象和 access_token)
        // 兼容处理：如果 apiResult 直接是 user 对象（旧版）或包含 user 对象（新版）
        const userObj = apiResult.user || apiResult;
        
        const newUser = {
          id: userObj.id,
          user_id: userObj.user_id,  // 重要：保存UUID用于积分和支付
          phone: userObj.phone,
          email: userObj.email,
          name: userObj.nickname,
          avatar: userObj.avatar,
          loginType: userObj.login_type,
          inviteCode: userData.inviteCode || null, // 添加邀请码信息
          inviterId: userData.inviterId || null,
          inviterName: userData.inviterName || null,
          created_at: userObj.created_at
        };

        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
        
        // 保存access_token
        if (apiResult.access_token) {
          localStorage.setItem('access_token', apiResult.access_token);
        }
        
        // 触发积分更新事件（注册时已赠送20积分）
        setTimeout(async () => {
          try {
            // 从后端获取真实的积分数据
            const pointsRes = await PointsAPI.getHistory(userObj.user_id);
            const actualBalance = pointsRes.balance || 0;
            const actualChange = pointsRes.entries?.[0]?.change || 0;
            
            window.dispatchEvent(new CustomEvent('pointsUpdated', {
              detail: { change: actualChange, newBalance: actualBalance }
            }));
          } catch (error) {
            console.warn('获取积分数据失败，使用默认值:', error);
            // 如果获取失败，使用默认值
            window.dispatchEvent(new CustomEvent('pointsUpdated', {
              detail: { change: 20, newBalance: 20 }
            }));
          }
        }, 500);
        
        // 如果有邀请码，记录邀请成功并发放积分（在用户创建成功后）
        if (userData.inviteCode) {
          try {
            await ReferralAPI.recordReferral({
              inviteCode: userData.inviteCode,
              inviterId: userData.inviterId,
              inviterName: userData.inviterName,
              invitedId: userObj.user_id,  // 使用UUID
              invitedName: newUser.name
            });
          } catch (error) {
            console.warn('邀请记录失败，但不影响注册:', error);
          }
        }
        
        return { success: true };
      } else {
        // 传统注册方式（包含验证码）
        const emailValidation = validateEmail(userData.email);
        if (!emailValidation.valid) {
          throw new Error(emailValidation.message);
        }
        
        const passwordValidation = validatePassword(userData.password);
        if (!passwordValidation.valid) {
          throw new Error(passwordValidation.message);
        }

        const codeValidation = validateVerificationCode(userData.code);
        if (!codeValidation.valid) {
          throw new Error(codeValidation.message);
        }

        // 调用后端API注册用户
        const registerData = {
          email: userData.email,
          nickname: userData.name || userData.email,
          password: userData.password,
          login_type: userData.loginType || 'email',
          avatar: userData.avatar || null,
          verification_code: userData.code
        };
        
        const apiResult = await authApi.register(registerData);
        
        // 兼容处理：如果 apiResult 直接是 user 对象（旧版）或包含 user 对象（新版）
        const userObj = apiResult.user || apiResult;

        const newUser = {
          id: userObj.id,
          user_id: userObj.user_id,  // 重要：保存UUID用于积分和支付
          email: userObj.email,
          name: userObj.nickname,
          avatar: userObj.avatar,
          loginType: userObj.login_type,
          inviteCode: userData.inviteCode || null,
          inviterId: userData.inviterId || null,
          inviterName: userData.inviterName || null,
          created_at: userObj.created_at
        };

        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
        
        // 保存access_token
        if (apiResult.access_token) {
          localStorage.setItem('access_token', apiResult.access_token);
        }
        
        // 触发积分更新事件（注册时已赠送20积分）
        setTimeout(async () => {
          try {
            // 从后端获取真实的积分数据
            const pointsRes = await PointsAPI.getHistory(userObj.user_id);
            const actualBalance = pointsRes.balance || 0;
            const actualChange = pointsRes.entries?.[0]?.change || 0;
            
            window.dispatchEvent(new CustomEvent('pointsUpdated', {
              detail: { change: actualChange, newBalance: actualBalance }
            }));
          } catch (error) {
            console.warn('获取积分数据失败，使用默认值:', error);
            // 如果获取失败，使用默认值
            window.dispatchEvent(new CustomEvent('pointsUpdated', {
              detail: { change: 20, newBalance: 20 }
            }));
          }
        }, 500);
        
        // 如果有邀请码，记录邀请成功并发放积分
        if (userData.inviteCode) {
          try {
            await ReferralAPI.recordReferral({
              inviteCode: userData.inviteCode,
              inviterId: userData.inviterId,
              inviterName: userData.inviterName,
              invitedId: userObj.user_id,  // 使用UUID
              invitedName: newUser.name
            });
          } catch (error) {
            console.warn('邀请记录失败，但不影响注册:', error);
          }
        }
        
        return { success: true };
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // 调用后端登出API，让后端清除服务器端的token和cookie中的refresh_token
      await authApi.logout();
    } catch (error) {
      // 即使API调用失败，也继续清理本地存储
    } finally {
      setUser(null);
      localStorage.removeItem('user');
      // 清除认证token
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      // 清除求职助手相关状态，确保重新登录时开启新对话
      localStorage.removeItem('searchLastVisit');
      localStorage.removeItem('currentSessionId');
      localStorage.removeItem('searchLastUserId');
      // 清除聊天会话相关的本地存储
      localStorage.removeItem('chatSessions');
      localStorage.removeItem('jobSearchResults');
      localStorage.removeItem('currentSearchQuery');
      // 清除搜索结果状态
      localStorage.removeItem('searchResults');
      localStorage.removeItem('searchQuery');
      // 清除验证码相关的本地存储
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('verificationCode_')) {
          localStorage.removeItem(key);
        }
      });
      // 清除其他可能的敏感数据
      localStorage.removeItem('interviewHistory');
      localStorage.removeItem('resumeHistory');
      localStorage.removeItem('meetingHistory');
      localStorage.removeItem('userPreferences');
      localStorage.removeItem('analyticsData');
    }
  };

  const updateUser = async (updatedUserData) => {
    try {
      // TODO: 调用后端API更新用户信息
      // const response = await api.updateUserProfile(updatedUserData);
      
      // 临时更新本地状态
      const updatedUser = { ...user, ...updatedUserData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return { success: true };
    } catch (error) {
      console.error('更新用户信息失败:', error);
      throw error;
    }
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    sendVerificationCode,
    sendForgetPassword,
    updateUser,
    validateInviteCode,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
